import React from "react";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";

const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return value;
};

const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
};

const parseJsonValue = (value, fallback) => {
    if (!value) return fallback;
    if (typeof value !== "string") return value;
    try {
        return JSON.parse(value);
    } catch (error) {
        return fallback;
    }
};

const getPicklistRows = (picklists) => {
    return (picklists || []).flatMap((picklist) => {
        const clubbedList = parseJsonValue(picklist.clubbed_list, []);
        if (!Array.isArray(clubbedList)) return [];

        return clubbedList.map((item, index) => ({
            key: `${picklist.id}-${index}`,
            picklist_id: picklist.id,
            serial_number: picklist.serial_number,
            item_code: item.name || item.item_code || "-",
            picked_qty: item.length || item.qty || 0,
            qr_count: Array.isArray(item.qr_list) ? item.qr_list.length : 0,
        }));
    });
};

const SummaryField = ({ label, value }) => (
    <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase text-gray-500">{label}</p>
        <p className="truncate text-sm font-semibold text-gray-900" title={String(formatValue(value))}>
            {formatValue(value)}
        </p>
    </div>
);

const ListingItemDetailsModal = ({ show, setShow, title, data, loading }) => {
    const details = data?.data || {};
    const order = details.order || {};
    const items = details.items || [];
    const invoices = details.invoices || [];
    const picklists = details.picklists || [];
    const visiblePicklists = details.type === "picklist" && details.record ? [details.record] : picklists;
    const picklistRows = getPicklistRows(visiblePicklists);

    return (
        <ModalComponent show={show} setShow={setShow} size="7xl">
            <div className="max-h-[82vh] overflow-y-auto bg-white">
                <div className="border-b border-gray-200 px-5 py-4">
                    <h2 className="text-lg font-bold text-gray-900">{title}</h2>
                    <p className="text-sm text-gray-600">Order No: {formatValue(order.order_no || details.record?.order_no)}</p>
                </div>

                {loading ? (
                    <div className="flex min-h-[260px] items-center justify-center text-sm font-semibold text-gray-600">
                        Loading details...
                    </div>
                ) : (
                    <div className="space-y-5 p-5">
                        <div className="grid grid-cols-2 gap-4 rounded-md border border-gray-200 bg-gray-50 p-4 md:grid-cols-4 lg:grid-cols-6">
                            <SummaryField label="Seller Code" value={order.seller_code} />
                            <SummaryField label="Buyer Code" value={order.buyer_code} />
                            <SummaryField label="Total SKU" value={order.total_sku} />
                            <SummaryField label="Order Qty" value={order.qty} />
                            <SummaryField label="Purchase Amount" value={order.purchase_amount} />
                            <SummaryField label="Created At" value={formatDateTime(order.created_at)} />
                        </div>

                        <section>
                            <div className="mb-2 flex items-center justify-between">
                                <h3 className="text-sm font-bold text-gray-900">Order Items</h3>
                                <span className="text-xs font-semibold text-gray-500">{items.length} item(s)</span>
                            </div>
                            <div className="overflow-x-auto rounded-md border border-gray-200">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-100 text-xs uppercase text-gray-600">
                                        <tr>
                                            <th className="px-3 py-2 text-left">Item Code</th>
                                            <th className="px-3 py-2 text-left">Part No</th>
                                            <th className="px-3 py-2 text-right">Qty</th>
                                            <th className="px-3 py-2 text-right">Picked Qty</th>
                                            <th className="px-3 py-2 text-left">Unit</th>
                                            <th className="px-3 py-2 text-right">Unit Price</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {items.length ? (
                                            items.map((item) => (
                                                <tr key={item.id}>
                                                    <td className="px-3 py-2 font-semibold text-gray-900">{formatValue(item.item_code)}</td>
                                                    <td className="px-3 py-2 text-gray-700">{formatValue(item.part_no)}</td>
                                                    <td className="px-3 py-2 text-right text-gray-700">{formatValue(item.qty)}</td>
                                                    <td className="px-3 py-2 text-right text-gray-700">{formatValue(item.picked_qty)}</td>
                                                    <td className="px-3 py-2 text-gray-700">{formatValue(item.unit)}</td>
                                                    <td className="px-3 py-2 text-right text-gray-700">{formatValue(item.unit_price)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td className="px-3 py-8 text-center text-gray-500" colSpan="6">
                                                    No items found for this order.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        <div className="grid gap-5 lg:grid-cols-2">
                            <section>
                                <div className="mb-2 flex items-center justify-between">
                                    <h3 className="text-sm font-bold text-gray-900">Invoices</h3>
                                    <span className="text-xs font-semibold text-gray-500">{invoices.length} invoice(s)</span>
                                </div>
                                <div className="space-y-2">
                                    {invoices.length ? invoices.map((invoice) => (
                                        <div key={invoice.id} className="rounded-md border border-gray-200 p-3">
                                            <div className="flex items-center justify-between gap-3">
                                                <p className="font-semibold text-gray-900">{invoice.invoice_no}</p>
                                                <p className="text-xs text-gray-500">{formatValue(invoice.status)}</p>
                                            </div>
                                            <p className="mt-1 text-xs text-gray-600">
                                                Date: {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : "-"} | Dispatch: {formatValue(invoice.dispatch_type)} | Out Code: {formatValue(invoice.out_code)}
                                            </p>
                                        </div>
                                    )) : (
                                        <div className="rounded-md border border-gray-200 p-4 text-sm text-gray-500">No invoices found.</div>
                                    )}
                                </div>
                            </section>

                            <section>
                                <div className="mb-2 flex items-center justify-between">
                                    <h3 className="text-sm font-bold text-gray-900">Picklist Items</h3>
                                    <span className="text-xs font-semibold text-gray-500">{visiblePicklists.length} picklist(s)</span>
                                </div>
                                <div className="max-h-72 overflow-auto rounded-md border border-gray-200">
                                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                                        <thead className="bg-gray-100 text-xs uppercase text-gray-600">
                                            <tr>
                                                <th className="px-3 py-2 text-left">Picklist</th>
                                                <th className="px-3 py-2 text-left">Item Code</th>
                                                <th className="px-3 py-2 text-right">Picked Qty</th>
                                                <th className="px-3 py-2 text-right">QR Count</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                            {picklistRows.length ? picklistRows.map((item) => (
                                                <tr key={item.key}>
                                                    <td className="px-3 py-2 text-gray-700">{item.serial_number || item.picklist_id}</td>
                                                    <td className="px-3 py-2 font-semibold text-gray-900">{item.item_code}</td>
                                                    <td className="px-3 py-2 text-right text-gray-700">{item.picked_qty}</td>
                                                    <td className="px-3 py-2 text-right text-gray-700">{item.qr_count}</td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td className="px-3 py-8 text-center text-gray-500" colSpan="4">
                                                        No picklist item data found.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        </div>
                    </div>
                )}
            </div>
        </ModalComponent>
    );
};

export default ListingItemDetailsModal;
